import { useCallback, useMemo } from 'react';

import {
  type PromoFeatureKey,
  type PromoFeatureRoleContext,
  getPromoFeaturesConfig,
} from '@luxuryescapes/contract-svc-promo';

import isProd from '~/utils/isProd';

import { useGetUserPromoRoles } from './apis/promo/useGetUserPromoRoles';
import useCurrentPromoBrand from './useCurrentPromoBrand';
import useCurrentUser from './useCurrentUser';
import { useSessionStorage } from './useSessionStorage';

// Catalogue keys are role-independent; access is evaluated with loaded roles in the hook below.
const optInFeatureKeys = new Set(
  getPromoFeaturesConfig().features.flatMap((feature) => (feature.canOptIn ? [feature.key] : [])),
);
const EMPTY_FEATURE_KEYS: Array<string> = [];
const OPT_IN_STORAGE_KEY_PREFIX = 'promo-feature-opt-ins';

type FeatureRoles = Pick<PromoFeatureRoleContext, 'authRoles' | 'promoRoles'>;

/** Resolve feature access from auth roles, cached promo roles, and this session's opt-ins. */
export function usePromoFeaturesConfig({ authRoles, promoRoles }: FeatureRoles = {}) {
  const { user } = useCurrentUser();
  const { promoBrand } = useCurrentPromoBrand();
  const { data: cachedPromoRoles } = useGetUserPromoRoles(user?.id_member ?? '', promoRoles === undefined);
  const [storedFeatureKeys, setStoredFeatureKeys] = useSessionStorage<Array<string>>(
    `${OPT_IN_STORAGE_KEY_PREFIX}:${user?.id_member ?? 'anonymous'}`,
    EMPTY_FEATURE_KEYS,
  );
  const optedInFeatureKeys = useMemo(
    () => new Set(storedFeatureKeys.filter((featureKey) => optInFeatureKeys.has(featureKey))),
    [storedFeatureKeys],
  );
  const setOptedIn = useCallback(
    (featureKey: string, optedIn: boolean) => {
      if (!optInFeatureKeys.has(featureKey)) {
        return;
      }

      setStoredFeatureKeys((currentFeatureKeys) => {
        const current = Array.isArray(currentFeatureKeys) ? currentFeatureKeys : [];
        const withoutFeature = current.filter((currentFeatureKey) => currentFeatureKey !== featureKey);
        return optedIn ? [...withoutFeature, featureKey] : withoutFeature;
      });
    },
    [setStoredFeatureKeys],
  );
  const config = useMemo(() => {
    const context = {
      brand: promoBrand,
      authRoles: authRoles ?? user?.roles,
      promoRoles: promoRoles ?? cachedPromoRoles,
      optIns: [...optedInFeatureKeys],
    };
    return getPromoFeaturesConfig(context);
  }, [authRoles, user?.roles, promoRoles, cachedPromoRoles, optedInFeatureKeys, promoBrand]);

  return { config, optedInFeatureKeys, setOptedIn };
}

/** Resolve contract access and environment availability before showing a feature's controls. */
export default function usePromoFeatureAvailability(featureKey: PromoFeatureKey, roles?: FeatureRoles): boolean {
  const { config } = usePromoFeaturesConfig(roles);
  const feature = config.features.find((entry) => entry.key === featureKey);

  return feature?.canUse === true && (!feature.isOnlyTest || !isProd());
}
